import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UserRound, Save, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { api } from "../../lib/game";
import { QUESTIONS } from "../../lib/questionnaire";
import { LIFE_STAGES, HOUSEHOLDS, PRIORITIES, Chip } from "./ProfileSetup";

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2.5">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export default function MyProfile({ onSaved, onBack }) {
  const [profile, setProfile] = useState(null);
  const [answers, setAnswers] = useState({});
  const [weekly, setWeekly] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/profile").then(({ data }) => {
      setProfile({ life_stage: data.life_stage, household_type: data.household_type, priorities: data.priorities || [] });
      setAnswers(Object.fromEntries((data.questionnaire || []).map((r) => [r.id, r.answer])));
      setWeekly(data.weekly_recap !== false);
    });
  }, []);

  const toggleWeekly = async (on) => {
    setWeekly(on);
    try {
      await api.put("/me/reminders", { enabled: on, kind: "weekly" });
      toast.success(`Weekly recap emails turned ${on ? "on" : "off"}.`);
    } catch (e) {
      setWeekly(!on);
      toast.error("Couldn't update your email setting.");
    }
  };

  if (!profile) return <div className="bg-grid min-h-[calc(100vh-61px)]" data-testid="my-profile-loading" />;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: p[k] === v ? null : v }));
  const togglePriority = (v) =>
    setProfile((p) => ({ ...p, priorities: p.priorities.includes(v) ? p.priorities.filter((x) => x !== v) : [...p.priorities, v] }));
  const pick = (id, opt) => setAnswers((a) => ({ ...a, [id]: a[id] === opt ? undefined : opt }));

  const save = async () => {
    setSaving(true);
    try {
      const responses = QUESTIONS.filter((q) => answers[q.id]).map((q) => ({ id: q.id, question: q.question, answer: answers[q.id] }));
      const { data } = await api.post("/me/onboarding", { ...profile, responses });
      toast.success(data.reshaped_sessions?.length ? "Saved — your just-started game has been updated too." : "Saved — your next game will use these answers.");
      onSaved?.(data.reshaped_sessions || []);
    } catch (e) {
      toast.error("Couldn't save your answers. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 flex justify-center">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="my-profile"
        className="max-w-2xl w-full rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-7 sm:p-9 h-fit">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
          <UserRound className="w-7 h-7 text-emerald-400" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-50">My profile</h1>
        <p className="text-slate-400 mt-2">Update your answers anytime — they shape your scenarios and how your starting money is split.</p>

        <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-950/40 p-4" data-testid="weekly-recap-row">
          <div className="flex items-start gap-3">
            <CalendarDays className="w-5 h-5 text-teal-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-100">Sunday weekly recap</p>
              <p className="text-xs text-slate-400">Every Sunday at 6pm: your week's games, wallet growth, salary progress and one-tap commitment check-ins.</p>
            </div>
          </div>
          <Switch data-testid="weekly-recap-switch" checked={weekly} onCheckedChange={toggleWeekly} />
        </div>

        <div className="mt-7 space-y-6">
          <Section title="Life stage">
            {LIFE_STAGES.map((s) => (
              <Chip key={s.v} testid={`profile-life-stage-${s.v}`} active={profile.life_stage === s.v} onClick={() => set("life_stage", s.v)}>{s.l}</Chip>
            ))}
          </Section>
          <Section title="Household">
            {HOUSEHOLDS.map((h) => (
              <Chip key={h.v} testid={`profile-household-${h.v}`} active={profile.household_type === h.v} onClick={() => set("household_type", h.v)}>{h.l}</Chip>
            ))}
          </Section>
          <Section title="Your priorities (pick any)">
            {PRIORITIES.map((p) => (
              <Chip key={p} testid={`profile-priority-${p}`} active={profile.priorities.includes(p)} onClick={() => togglePriority(p)}>{p}</Chip>
            ))}
          </Section>
          <div className="border-t border-slate-800 pt-6 space-y-6" data-testid="profile-questionnaire">
            {QUESTIONS.map((q) => (
              <Section key={q.id} title={q.question}>
                {q.options.map((o, i) => (
                  <Chip key={o} testid={`profile-q-${q.id}-${i}`} active={answers[q.id] === o} onClick={() => pick(q.id, o)}>{o}</Chip>
                ))}
              </Section>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={onBack} data-testid="my-profile-back-btn" className="text-sm text-slate-400 hover:text-slate-200 underline underline-offset-4">Back</button>
          <Button onClick={save} disabled={saving} data-testid="my-profile-save-btn"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-semibold rounded-xl px-6 h-11">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save answers"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
